import React, { useEffect, useState } from 'react';

import { CreditCard, Mail, Sparkles } from 'lucide-react';

import {
  FREE_COURSES_PLAYED_LIMIT,
  MemberTypeConstants,
} from '../../constants/MemberTypeConstants';
import { useMemberApi } from '../../hooks/useMemberApi';
import { useSetMyEmailPreference } from '../../hooks/useMemberProfileApi';
import {
  MembershipStatusDTO,
  MemberProfileDTO,
} from '../../services/apiClient';
import { showToast } from '../../utils/showToast';
import Badge from '../common/Badge';
import Button from '../common/Button';
import Card from '../common/Card';
import UpgradeDrawer from '../drawers/UpgradeDrawer';

interface AccountSettingsProps {
  profile: MemberProfileDTO;
}

const formatDate = (value: Date | string | undefined): string | null => {
  if (value == null) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 py-3 first:border-t-0 first:pt-0 dark:border-white/10">
    <span className="text-sm text-ink-muted dark:text-white/60">{label}</span>
    <span className="text-sm font-medium text-ink dark:text-white">
      {children}
    </span>
  </div>
);

const linkButton =
  'inline-flex items-center gap-1.5 text-sm font-semibold text-primary-deep hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-wait disabled:opacity-60 dark:text-primary';

// The Membership and Notifications cards on the member's profile. Payment details live with
// Stripe: "Edit payment details" opens Stripe's own billing portal, the same one the dashboard's
// Manage Membership opens, so no card number ever touches GolfPlayed.
const AccountSettings: React.FC<AccountSettingsProps> = ({ profile }) => {
  const { createBillingPortalSession, getMyMembershipStatus } = useMemberApi();
  const setEmailPreference = useSetMyEmailPreference();

  const [status, setStatus] = useState<MembershipStatusDTO | null>(null);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);

  useEffect((): (() => void) => {
    let cancelled = false;

    getMyMembershipStatus()
      .then((response) => {
        if (!cancelled && response?.success && response.data) {
          setStatus(response.data);
        }
      })
      .catch(() => {
        // The card just shows less. Nothing else on the page depends on it.
      });

    return () => {
      cancelled = true;
    };
    // Once on mount: getMyMembershipStatus is rebuilt every render by useMemberApi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Case-insensitive, per CLAUDE.md: GUID casing is not guaranteed to survive a round trip.
  const memberTypeId = status?.memberTypeId?.toLowerCase();
  const isLifetime =
    memberTypeId === MemberTypeConstants.LIFETIME_ID.toLowerCase();
  const isFree = memberTypeId === MemberTypeConstants.FREE_ID.toLowerCase();
  const renewal =
    isLifetime || isFree ? null : formatDate(status?.paidUntilUtc);

  const openBillingPortal = async (): Promise<void> => {
    setIsOpeningPortal(true);
    try {
      const response = await createBillingPortalSession();
      if (response?.success && response.data) {
        window.location.href = response.data;
        return;
      }
      showToast(
        response?.message ?? 'Could not open your payment details.',
        'error',
      );
    } catch {
      showToast('Could not open your payment details.', 'error');
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const toggleEmail = (): void => {
    const next = !profile.receiveEmails;
    setEmailPreference.mutate(next, {
      onSuccess: () =>
        showToast(next ? 'Email is on.' : 'Email is off.', 'success'),
      onError: () => showToast('Could not save that change.', 'error'),
    });
  };

  return (
    <>
      <Card title="Membership">
        <Row label="Your plan">
          {status?.memberTypeName ? (
            <Badge label={status.memberTypeName} variant="brand" />
          ) : (
            '-'
          )}
        </Row>

        {renewal != null && <Row label="Your renewal">{renewal}</Row>}
        {isLifetime && <Row label="Your renewal">Never, it is for life</Row>}

        {!isLifetime && (
          <Row label="Payment method">
            {profile.hasBillingAccount ? (
              <button
                type="button"
                onClick={() => void openBillingPortal()}
                disabled={isOpeningPortal}
                className={linkButton}
              >
                <CreditCard className="h-4 w-4" />
                {isOpeningPortal ? 'Opening...' : 'Edit payment details'}
              </button>
            ) : (
              <span className="font-normal text-ink-muted dark:text-white/60">
                None yet
              </span>
            )}
          </Row>
        )}

        {isFree && (
          <div className="mt-3 rounded-lg bg-primary/10 p-4">
            <p className="text-sm text-ink dark:text-white">
              The free plan holds {FREE_COURSES_PLAYED_LIMIT} courses played.
              Upgrade to add as many as you like.
            </p>
            <Button
              onClick={() => setIsUpgradeOpen(true)}
              icon={Sparkles}
              className="mt-3"
            >
              Upgrade
            </Button>
          </div>
        )}
      </Card>

      <Card title="Notifications">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Mail className="mt-0.5 h-5 w-5 shrink-0 text-ink-muted dark:text-white/60" />
            <div>
              <p className="text-sm font-medium text-ink dark:text-white">
                Email
              </p>
              <p className="text-xs text-ink-muted dark:text-white/50">
                News and updates from GolfPlayed. Account emails, like receipts,
                always come.
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={profile.receiveEmails === true}
            aria-label="Email"
            onClick={toggleEmail}
            disabled={setEmailPreference.isPending}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 ${
              profile.receiveEmails
                ? 'bg-primary'
                : 'bg-gray-300 dark:bg-white/20'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                profile.receiveEmails ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </Card>

      <UpgradeDrawer
        isOpen={isUpgradeOpen}
        message={null}
        onClose={() => setIsUpgradeOpen(false)}
      />
    </>
  );
};

export default AccountSettings;
